/**
 * The external viewer (f15.5): an opaque link, a password gate, the document.
 * Mirrors `artifact-viewer.html`.
 *
 * Rendered OUTSIDE the MSAL provider, the session context and the app shell
 * (see `main.tsx`). The page makes no request until a code is submitted, and
 * its DOM does not depend on the link: an existing artifact, an unknown UUID
 * and garbage all render the same gate (BR-F15.5-02).
 *
 * The code and the view grant live in component state only -- never in the
 * URL, storage, cookies, the query cache key or a log line.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, Unlock, X } from 'lucide-react';
import { useParams } from 'react-router-dom';

import {
  UNIFORM_REFUSAL,
  enterAccessCode,
  externalApiUrl,
  type ExternalAccessGranted,
} from '@/api/externalViewer';

import '@/styles/external-viewer.css';

const SESSION_ENDED = 'Your session ended. Enter the access code again.';

const PUBLISHED_DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Page-level metadata: title, no referrer, no indexing, standalone layout. */
function useStandaloneDocument() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Protected document';
    document.body.classList.add('standalone');
    const metas = (
      [
        ['referrer', 'no-referrer'],
        ['robots', 'noindex, nofollow'],
      ] as const
    ).map(([name, content]) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    });
    return () => {
      document.title = previousTitle;
      document.body.classList.remove('standalone');
      metas.forEach((meta) => meta.remove());
    };
  }, []);
}

function DocumentModal({ granted, onClose }: { granted: ExternalAccessGranted; onClose: () => void }) {
  const published = PUBLISHED_DATE.format(new Date(granted.published_at));
  const isPdf = granted.content_type === 'application/pdf';
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="external-doc-title">
      <div className="modal xwide">
        <div className="modal-head">
          <div>
            <h3 id="external-doc-title">{granted.title}</h3>
            <div className="m-sub">
              Version {granted.version_number} &middot; published {published}
            </div>
          </div>
          <div className="btn-row">
            <a className="btn btn-secondary btn-sm" href={externalApiUrl(granted.download_url)} rel="noreferrer">
              <Download aria-hidden="true" /> Download
            </a>
            <button type="button" className="icon-btn" aria-label="Close document" onClick={onClose}>
              <X aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="modal-body">
          {isPdf && <p className="hint external-doc-hint">If the document does not display, use Download.</p>}
          <iframe
            className="external-doc-frame"
            sandbox=""
            referrerPolicy="no-referrer"
            title="Protected document"
            src={externalApiUrl(granted.content_url)}
          />
        </div>
      </div>
    </div>
  );
}

export function ExternalViewerPage() {
  // The ref is read only when a code is submitted; it never changes the DOM.
  const { ref = '' } = useParams();
  const [code, setCode] = useState('');
  const [granted, setGranted] = useState<ExternalAccessGranted | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useStandaloneDocument();

  const entry = useMutation({
    mutationFn: (entered: string) => enterAccessCode(ref, entered),
    onSuccess: (result) => {
      setError(null);
      setNotice(null);
      setGranted(result);
    },
    onError: () => {
      // One message for every cause, and the input is cleared (BR-F15.5-03).
      setError(UNIFORM_REFUSAL);
      input.current?.focus();
    },
    onSettled: () => setCode(''),
  });

  // Back to the gate when the view grant expires.
  useEffect(() => {
    if (!granted) return undefined;
    const remaining = new Date(granted.grant_expires_at).getTime() - Date.now();
    const timer = window.setTimeout(
      () => {
        setGranted(null);
        setNotice(SESSION_ENDED);
      },
      Math.max(remaining, 0),
    );
    return () => window.clearTimeout(timer);
  }, [granted]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || entry.isPending) return;
    entry.mutate(code);
  };

  return (
    <main className="external-viewer">
      <div className="auth-card external-gate">
        <div className="external-gate-head">
          <div className="external-gate-title">Protected document</div>
          <div className="hint">Enter the access code you were given</div>
        </div>
        <div className="card">
          <form className="card-body" onSubmit={submit} noValidate>
            {notice && (
              <p className="hint external-gate-notice" role="status">
                {notice}
              </p>
            )}
            <div className="field mb-4">
              <label htmlFor="external-code">Access code</label>
              <input
                ref={input}
                id="external-code"
                className="external-code-input"
                type="password"
                placeholder="••••••••"
                autoComplete="off"
                spellCheck={false}
                value={code}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'external-code-error' : undefined}
                onChange={(event) => {
                  setCode(event.target.value);
                  if (error) setError(null);
                }}
              />
              {error && (
                <p id="external-code-error" className="field-error" role="alert">
                  {error}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary external-open"
              disabled={!code.trim() || entry.isPending}
            >
              <Unlock aria-hidden="true" /> Open document
            </button>
            <p className="hint external-gate-fine">Codes are checked in constant time and entry is rate limited.</p>
          </form>
        </div>
        <p className="hint external-gate-fine">
          This page is not part of any sign-in system. If you do not have a code, contact the person who sent you
          the link.
        </p>
      </div>

      {granted && <DocumentModal granted={granted} onClose={() => setGranted(null)} />}
    </main>
  );
}
